/**
 * Per-bottle interaction for `#sample-kit-model`.
 *
 * model-viewer has no public API for rotating individual nodes, so this file
 * reaches into its internal Three.js scene via `Symbol("scene")` and
 * `Symbol("needsRender")` (descriptions survive in vendored 4.3.1). That is
 * unofficial and will break if those internals are renamed; the durable
 * alternative is a baked GLB clip plus `autoplay`.
 *
 * The collection callout is wired first, outside `boot()`. Bottle motion
 * only starts after the model has loaded and the four `Bottle Handle Final`
 * groups exist.
 */
(() => {
  const mv = document.querySelector("#sample-kit-model");
  // Snap instead of easing when the user has asked the OS to cut motion.
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;
  // Idle "rock": ±15° on local Y, 8s sine cycle, starts at 0 (front-facing).
  const OSCILLATE_AMP = (15 * Math.PI) / 180;
  const OSCILLATE_PERIOD = 8;
  // Idle "glance": max yaw/pitch toward the cursor, eased so a still pointer
  // settles and stops dirtying frames (cheaper at rest than rock).
  const GLANCE_YAW = (22 * Math.PI) / 180;
  const GLANCE_PITCH = (8 * Math.PI) / 180;
  const GLANCE_EASE = 0.12;
  // Pixel-space travels, converted to world units from the live camera FOV
  // so they stay the same on-screen size across viewports.
  const LIFT_PX = 10;
  const FRONT_PX = 90;
  const RECEDE_PX = 160;
  const LIFT_EASE = 0.15;
  const ROTATE_EASE = 0.2;
  // Distinguishes a tap (open callout) from a bottle-drag (spin on Y).
  const TAP_MAX_MOVE = 6;
  const TAP_MAX_MS = 500;
  // Positive so dragging right rotates the bottle the way you expect.
  const DRAG_ROTATE = 0.012;
  const SELECT_SCALE = 1.07;
  // GLB groups that own a whole bottle (mesh + cap + label).
  const HANDLE_NAME = /Bottle[_ ]Handle[_ ]Final/i;
  // Fallback order if a node name is missing; primary map matches the
  // framed `camera-orbit` view (left-to-right is not GLB export order).
  const COLLECTION_ORDER = ["omni", "brewgas", "skyfarm", "quantum"];
  const COLLECTION_BY_NODE = {
    bottlehandlefinal010: "omni",
    bottlehandlefinal037: "brewgas",
    bottlehandlefinal038: "skyfarm",
    bottlehandlefinal043: "quantum",
  };
  // Same breakpoint as the stacked sample layout. Callout cards stay desktop-only.
  const mobileCards = window.matchMedia("(width <= 940px)");
  // model-viewer `loading="lazy"` uses a 0px IntersectionObserver, so the
  // poster vials stay up until the GLB starts after the kit is already on
  // screen. Flip to eager a viewport early so the 3D kit is ready on arrival.
  const PRELOAD_ROOT_MARGIN = "1000px 0px";

  function preloadModel() {
    if (!mv || mv.getAttribute("loading") === "eager") return;
    mv.setAttribute("loading", "eager");
    if ("loading" in mv) mv.loading = "eager";
  }

  if (mv) {
    if ("IntersectionObserver" in window) {
      const preload = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          preloadModel();
          preload.disconnect();
        },
        { rootMargin: PRELOAD_ROOT_MARGIN },
      );
      preload.observe(mv);
    } else {
      preloadModel();
    }
  }

  /**
   * Non-modal collection callout. Uses `dialog.show()` (no backdrop) so the
   * 3D kit stays visible. Copy lives in HTML `#collection-*` templates, not
   * here, so marketing can edit it next to the rest of the page.
   */
  function createDialogApi() {
    const dialog = document.querySelector("#collection-dialog");
    const body = dialog?.querySelector(".collection-dialog-body");
    let lastFocused = null;

    function open(key) {
      if (mobileCards.matches) return;
      const tpl = document.querySelector(`#collection-${key}`);
      if (!dialog || !body || !tpl) return;
      const wasOpen = dialog.open;
      if (!wasOpen) lastFocused = document.activeElement;
      dialog.dataset.collection = key;
      body.replaceChildren(tpl.content.cloneNode(true));
      // `show()`, not `showModal()`: no dimmed backdrop, no focus trap that
      // would steal pointer events from the bottles.
      if (!wasOpen && typeof dialog.show === "function") dialog.show();
      if (!wasOpen) dialog.querySelector(".collection-dialog-close")?.focus();
    }

    function close() {
      if (dialog?.open) dialog.close();
    }

    dialog
      ?.querySelector(".collection-dialog-close")
      ?.addEventListener("click", close);

    // Click-outside dismiss, but ignore the viewer so you can pick another
    // bottle without the callout closing first.
    document.addEventListener("pointerdown", (event) => {
      if (!dialog?.open) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (dialog.contains(target)) return;
      if (typeof target.closest === "function") {
        if (target.closest("#sample-kit-model")) return;
      }
      close();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && dialog?.open) close();
    });

    dialog?.addEventListener("close", () => {
      const restore = lastFocused;
      lastFocused = null;
      if (restore && typeof restore.focus === "function") restore.focus();
    });

    if (typeof mobileCards.addEventListener === "function") {
      mobileCards.addEventListener("change", () => {
        if (mobileCards.matches) close();
      });
    }

    return {
      open,
      close,
      isOpen: () => !!dialog?.open,
      selectedKey: () => (dialog?.open ? dialog.dataset.collection : null),
    };
  }

  const dialogApi = createDialogApi();

  if (!mv) return;

  /**
   * model-viewer's Three.js handles are unique Symbols. The minifier kept the
   * descriptions ("scene", "needsRender") in 4.3.1, but they live on the
   * prototype, not the element instance, so we walk the chain.
   */
  function findSymbol(target, description) {
    let current = target;
    while (current && current !== Object.prototype) {
      const match = Object.getOwnPropertySymbols(current).find(
        (symbol) => symbol.description === description,
      );
      if (match) return match;
      current = Object.getPrototypeOf(current);
    }
    return undefined;
  }

  function isBottleHandle(obj) {
    const names = [obj.userData?.name, obj.name].filter(Boolean);
    return names.some((name) => HANDLE_NAME.test(name));
  }

  // GLB names look like "Bottle Handle Final.043"; strip punctuation so the
  // lookup key is stable across exporter spacing/casing.
  function normalizeName(name) {
    return String(name)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  }

  function collectionKeyFor(obj, index) {
    const names = [obj.userData?.name, obj.name].filter(Boolean);
    for (const name of names) {
      const key = COLLECTION_BY_NODE[normalizeName(name)];
      if (key) return key;
    }
    return COLLECTION_ORDER[index] || null;
  }

  /**
   * Find the four bottle roots and snapshot the load pose. We clone Three.js
   * Vector3/Quaternion objects off the node itself rather than importing
   * three — this file has no bundler.
   */
  function collectBottles(scene) {
    const bottles = [];
    const seen = new Set();

    function add(obj) {
      if (!obj || seen.has(obj)) return;
      seen.add(obj);
      bottles.push({
        node: obj,
        baseY: obj.position.y,
        basePos: obj.position.clone(),
        lift: 0,
        depthT: 0,
        scaleT: 1,
        key: null,
        restQuat: obj.quaternion.clone(),
        glanceTarget: obj.quaternion.clone(),
        yawQuat: obj.quaternion.clone(),
        pitchQuat: obj.quaternion.clone(),
        axisX: obj.position.clone().set(1, 0, 0),
        axisY: obj.position.clone().set(0, 1, 0),
        baseScale: obj.scale.clone(),
        oscTime: 0,
      });
    }

    if (typeof scene.traverse === "function") {
      scene.traverse((obj) => {
        if (isBottleHandle(obj)) add(obj);
      });
    }

    // Older / internal scene shapes store the model under `.models` or
    // `.model` instead of being traversable themselves.
    if (!bottles.length) {
      const roots = scene.models || (scene.model ? [scene.model] : []);
      for (const root of roots) {
        if (!root) continue;
        if (typeof root.traverse === "function") {
          root.traverse((obj) => {
            if (isBottleHandle(obj)) add(obj);
          });
        }
        if (!bottles.length && root.children) {
          for (const child of root.children) {
            if (child.children && child.children.length) add(child);
          }
        }
      }
    }

    bottles.forEach((bottle, index) => {
      bottle.key = collectionKeyFor(bottle.node, index);
    });

    return bottles;
  }

  function boot() {
    const sceneSym = findSymbol(mv, "scene");
    if (!sceneSym) return;

    const scene = mv[sceneSym];
    if (!scene) return;

    const bottles = collectBottles(scene);
    if (!bottles.length) return;

    const renderSym = findSymbol(mv, "needsRender");
    // Glance tracking is scoped to the sample band, not the whole page, so
    // bottles square up when you leave the section instead of pinning at
    // full deflection while you read the header.
    const section = mv.closest(".sample-section") || mv;
    // Scratch Vector3s reused every frame so the rAF loop does not allocate.
    const projected = bottles[0].node.position.clone();
    const vA = bottles[0].node.position.clone();
    const vB = bottles[0].node.position.clone();
    const vC = bottles[0].node.position.clone();
    let hovered = null;
    let dragging = null;
    let lastPointerX = 0;
    let lastTime = performance.now();
    let pointerStart = null;
    let cursor = null;

    function idleMotion() {
      return mv.dataset.idleMotion === "glance" ? "glance" : "rock";
    }

    // Tell model-viewer its scene changed. We have to poke both APIs because
    // which one actually schedules a draw has shifted across versions.
    function markDirty() {
      if (typeof scene.queueRender === "function") scene.queueRender();
      if (renderSym && typeof mv[renderSym] === "function") mv[renderSym]();
    }

    // `data-idle-motion` can be flipped live in the inspector; reset the
    // sine clock so rock starts from the front rather than mid-cycle.
    new MutationObserver(() => {
      for (const bottle of bottles) bottle.oscTime = 0;
      markDirty();
    }).observe(mv, { attributes: true, attributeFilter: ["data-idle-motion"] });

    function getCamera() {
      return typeof scene.getCamera === "function"
        ? scene.getCamera()
        : scene.camera;
    }

    // World units that currently cover one CSS pixel at this bottle's depth.
    function worldPerPx(bottle) {
      const camera = getCamera();
      bottle.node.getWorldPosition(vA);
      const dist = camera.position.distanceTo(vA);
      const fov = (camera.fov * Math.PI) / 180;
      return (2 * dist * Math.tan(fov / 2)) / Math.max(mv.clientHeight, 1);
    }

    // Hover lift is a local-Y write, so divide out the parent's world scale
    // or a scaled group would overshoot the 10px target.
    function liftAmount(bottle) {
      let parentScaleY = 1;
      if (bottle.node.parent && typeof bottle.node.parent.getWorldScale === "function") {
        bottle.node.parent.getWorldScale(vB);
        parentScaleY = vB.y || 1;
      }
      return (LIFT_PX * worldPerPx(bottle)) / parentScaleY;
    }

    /**
     * Unit vector from the bottle toward the camera, expressed in the
     * parent's local space (node.position is local). worldToLocal also
     * converts world units to local units, so the caller must not divide
     * by parent scale again. Recomputed each frame so a selected bottle
     * keeps facing the viewer if the camera ever moves.
     */
    function towardCameraLocal(bottle) {
      const camera = getCamera();
      bottle.node.getWorldPosition(vA);
      vB.copy(camera.position).sub(vA);
      if (typeof vB.lengthSq === "function" ? vB.lengthSq() > 1e-12 : true) {
        vB.normalize();
      } else {
        vB.set(0, 0, 1);
      }
      const parent = bottle.node.parent;
      if (parent && typeof parent.worldToLocal === "function") {
        vC.copy(vA).add(vB);
        parent.worldToLocal(vC);
        parent.worldToLocal(vA);
        return vC.sub(vA);
      }
      return vB;
    }

    function screenPosition(bottle) {
      const camera =
        typeof scene.getCamera === "function" ? scene.getCamera() : scene.camera;
      const rect = mv.getBoundingClientRect();
      bottle.node.getWorldPosition(projected);
      projected.project(camera);
      return {
        x: rect.left + ((projected.x + 1) / 2) * rect.width,
        y: rect.top + ((1 - projected.y) / 2) * rect.height,
      };
    }

    /**
     * model-viewer's hit test only tells us "something was hit". Map that
     * hit to the nearest bottle origin in screen space so overlapping
     * meshes still pick the intended bottle.
     */
    function pickBottle(event) {
      if (typeof mv.positionAndNormalFromPoint !== "function") return null;
      const hit = mv.positionAndNormalFromPoint(event.clientX, event.clientY);
      if (!hit) return null;

      let best = null;
      let bestDist = Infinity;
      for (const bottle of bottles) {
        const pos = screenPosition(bottle);
        const dx = event.clientX - pos.x;
        const dy = event.clientY - pos.y;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          best = bottle;
        }
      }
      return best;
    }

    /**
     * Idle glance: offset from this bottle's screen origin, clamped to the
     * viewer half-size so deflection is resolution-independent, then apply
     * as local yaw then pitch on top of the load pose. Same yaw sign as
     * DRAG_ROTATE so the bottles turn the way the pointer moves.
     */
    function glanceToward(bottle) {
      bottle.oscTime = 0;
      let ndx = 0;
      let ndy = 0;
      if (cursor) {
        const pos = screenPosition(bottle);
        const rect = mv.getBoundingClientRect();
        const halfW = Math.max(rect.width / 2, 1);
        const halfH = Math.max(rect.height / 2, 1);
        ndx = Math.max(-1, Math.min(1, (cursor.x - pos.x) / halfW));
        ndy = Math.max(-1, Math.min(1, (cursor.y - pos.y) / halfH));
      }
      const target = bottle.glanceTarget;
      target.copy(bottle.restQuat);
      bottle.yawQuat.setFromAxisAngle(bottle.axisY, GLANCE_YAW * ndx);
      target.multiply(bottle.yawQuat);
      bottle.pitchQuat.setFromAxisAngle(bottle.axisX, GLANCE_PITCH * ndy);
      target.multiply(bottle.pitchQuat);

      const q = bottle.node.quaternion;
      const remaining =
        typeof q.angleTo === "function" ? q.angleTo(target) : 1;
      if (remaining > 1e-4) {
        q.slerp(target, GLANCE_EASE);
        return true;
      }
      return false;
    }

    function heldBottle() {
      return dragging || hovered;
    }

    // Label-forward pose stored at load. Hover and selection both use this
    // so a featured bottle never shows its back while the callout is open.
    function easeToRest(bottle) {
      bottle.oscTime = 0;
      if (!bottle.restQuat) return false;
      const q = bottle.node.quaternion;
      const remaining =
        typeof q.angleTo === "function" ? q.angleTo(bottle.restQuat) : 1;
      if (remaining > 1e-4) {
        if (reducedMotion) q.copy(bottle.restQuat);
        else q.slerp(bottle.restQuat, ROTATE_EASE);
        return true;
      }
      return false;
    }

    function tick(now) {
      const dt = Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;
      let changed = false;
      const selectedKey = dialogApi.selectedKey();

      for (const bottle of bottles) {
        const selected = bottle.key && bottle.key === selectedKey;

        // Branch order: drag wins, then hover, then selected hold, then idle.
        // An in-progress drag must not slerp, or the pointer would fight us.
        if (bottle === dragging) {
          /* rotation comes from pointermove */
        } else if (bottle === hovered) {
          if (easeToRest(bottle)) changed = true;
        } else if (selected) {
          if (easeToRest(bottle)) changed = true;
        } else if (!reducedMotion && bottle.restQuat) {
          if (idleMotion() === "glance") {
            if (glanceToward(bottle)) changed = true;
          } else {
            bottle.oscTime += dt * ((Math.PI * 2) / OSCILLATE_PERIOD);
            bottle.node.quaternion.copy(bottle.restQuat);
            bottle.node.rotateY(OSCILLATE_AMP * Math.sin(bottle.oscTime));
            changed = true;
          }
        }

        const target = bottle === heldBottle() ? liftAmount(bottle) : 0;
        const nextLift = reducedMotion
          ? target
          : bottle.lift + (target - bottle.lift) * LIFT_EASE;
        if (Math.abs(nextLift - bottle.lift) > 1e-6) changed = true;
        bottle.lift = nextLift;

        // depthT is unitless: +1 selected (FRONT_PX toward camera), -1 the
        // other three (RECEDE_PX away) so they cannot collide with the
        // bottle coming forward. 0 when no callout is open.
        const targetDepth = selectedKey ? (selected ? 1 : -1) : 0;
        const nextDepth = reducedMotion
          ? targetDepth
          : bottle.depthT + (targetDepth - bottle.depthT) * LIFT_EASE;
        if (Math.abs(nextDepth - bottle.depthT) > 1e-6) changed = true;
        bottle.depthT = nextDepth;
        const px =
          bottle.depthT >= 0
            ? bottle.depthT * FRONT_PX
            : bottle.depthT * RECEDE_PX;

        // Always rebuild from the load pose so lift and depth compose
        // instead of accumulating on last frame's position.
        const pos = bottle.node.position;
        const base = bottle.basePos;
        pos.copy(base);
        pos.y = bottle.baseY + bottle.lift;
        if (Math.abs(px) > 1e-6) {
          const dir = towardCameraLocal(bottle);
          const localDist = px * worldPerPx(bottle);
          pos.x += dir.x * localDist;
          pos.y += dir.y * localDist;
          pos.z += dir.z * localDist;
        }

        const targetScale = selected ? SELECT_SCALE : 1;
        const nextScale = reducedMotion
          ? targetScale
          : bottle.scaleT + (targetScale - bottle.scaleT) * LIFT_EASE;
        if (Math.abs(nextScale - bottle.scaleT) > 1e-6) changed = true;
        bottle.scaleT = nextScale;
        const scale = bottle.baseScale;
        if (scale) {
          bottle.node.scale.set(
            scale.x * nextScale,
            scale.y * nextScale,
            scale.z * nextScale,
          );
        }
      }

      if (changed) markDirty();
      requestAnimationFrame(tick);
    }

    const dialog = document.querySelector("#collection-dialog");
    dialog?.addEventListener("close", () => {
      hovered = null;
      dragging = null;
      mv.classList.remove("is-bottle-hover", "is-bottle-drag");
    });

    function endBottleDrag(event) {
      const start = pointerStart;
      pointerStart = null;
      dragging = null;
      mv.classList.remove("is-bottle-drag");
      if (
        event &&
        typeof mv.hasPointerCapture === "function" &&
        mv.hasPointerCapture(event.pointerId)
      ) {
        mv.releasePointerCapture(event.pointerId);
      }
      return start;
    }

    // Capture phase so we see the hit before model-viewer's (now unused)
    // camera-controls would. Pointer capture keeps rotate events if the
    // cursor leaves the canvas mid-drag.
    mv.addEventListener(
      "pointerdown",
      (event) => {
        pointerStart = {
          x: event.clientX,
          y: event.clientY,
          t: performance.now(),
          id: event.pointerId,
        };
        const bottle = pickBottle(event);
        if (!bottle) return;
        dragging = bottle;
        hovered = bottle;
        lastPointerX = event.clientX;
        mv.classList.add("is-bottle-hover", "is-bottle-drag");
        try {
          mv.setPointerCapture(event.pointerId);
        } catch (_) {
          /* capture is optional */
        }
        event.stopPropagation();
      },
      true,
    );

    mv.addEventListener(
      "pointermove",
      (event) => {
        if (dragging && pointerStart && pointerStart.id === event.pointerId) {
          const dx = event.clientX - lastPointerX;
          lastPointerX = event.clientX;
          if (dx) {
            dragging.node.rotateY(dx * DRAG_ROTATE);
            markDirty();
          }
          event.stopPropagation();
          return;
        }
        hovered = pickBottle(event);
        mv.classList.toggle("is-bottle-hover", !!hovered);
      },
      true,
    );

    mv.addEventListener(
      "pointerup",
      (event) => {
        const start = endBottleDrag(event);
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        const dist = Math.hypot(dx, dy);
        const held = performance.now() - start.t;
        // A short, still press is a tap; anything else was a spin gesture.
        if (dist > TAP_MAX_MOVE || held > TAP_MAX_MS) return;
        const bottle = pickBottle(event);
        if (bottle?.key) dialogApi.open(bottle.key);
      },
      true,
    );

    mv.addEventListener(
      "pointercancel",
      (event) => {
        endBottleDrag(event);
      },
      true,
    );

    mv.addEventListener("pointerleave", () => {
      if (dragging) return;
      hovered = null;
      mv.classList.remove("is-bottle-hover");
    });

    // Bubble-phase, separate from the capture listeners on `mv`, so drag
    // still wins on the canvas. Ignore touch so a tap does not leave the
    // bottles frozen mid-glance.
    section.addEventListener(
      "pointermove",
      (event) => {
        if (event.pointerType && event.pointerType !== "mouse") return;
        cursor = { x: event.clientX, y: event.clientY };
      },
      { passive: true },
    );

    section.addEventListener("pointerleave", (event) => {
      if (event.pointerType && event.pointerType !== "mouse") return;
      cursor = null;
    });

    requestAnimationFrame(tick);
  }

  // `loading="lazy"` means the custom element can upgrade before the GLB
  // exists; wait for `load` so collectBottles sees real nodes.
  function onDefined() {
    if (mv.loaded) boot();
    else mv.addEventListener("load", boot, { once: true });
  }

  if (customElements.get("model-viewer")) onDefined();
  else customElements.whenDefined("model-viewer").then(onDefined);
})();
